import { useState } from 'react';
import { Button, Form, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import Modal from 'react-modal';
import { getPackagingCosts } from '../helpers/api';
import {
  accessTokenAtom,
  userIdAtom,
  loggedInUserAtom,
} from '../state';
import { useRecoilValue } from 'recoil';
import { round } from '../helpers/misc';
import Logger from '../helpers/Logger';

const logger = new Logger();

const PackagingCostsSearchModal = props => {
  const { isOpen, hide, onSubmit } = props;
  const [partNumberSearchValue, setPartNumberSearchValue] = useState('');
  const [partDescriptionSearchValue, setPartDescriptionSearchValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const accessToken = useRecoilValue(accessTokenAtom);
  const userId = useRecoilValue(userIdAtom);
  const loggedInUser = useRecoilValue(loggedInUserAtom);
  
  const searchPackaging = async () => {
    try {
      setIsSearching(true);
      const parts = await getPackagingCosts(accessToken, partNumberSearchValue, partDescriptionSearchValue);
      setSearchResults(parts);
      setHasSearched(true);
    }
    catch(e) {
      console.error('PackagingCostsSearchModal.searchPackaging error ->', e);
      logger.log(accessToken, `PackagingCostsSearchModal.searchPackaging error -> ${JSON.stringify(e)}`, userId, loggedInUser);
      setSearchResults([]);
      setHasSearched(true);
    }
    finally {
      setIsSearching(false);
    }
  };
  
  return (
    <Modal
      isOpen={isOpen}
      style={styles.partsModal}
      ariaHideApp={false}
    >
      <div style={styles.partsModalHeaderRow}>
        <h4 style={{ margin: 0 }}>Search Packaging</h4>
        <FontAwesomeIcon
          icon={faTimes}
          size="lg"
          onClick={() => {
            setPartNumberSearchValue('');
            setPartDescriptionSearchValue('');
            setSearchResults([]);
            setHasSearched(false);
            hide();
          }}
          style={styles.closeTimes}
        />
      </div>
      <div style={styles.partsModalHeaderRow}>
        <div style={styles.partsModalSearchGroup}>
          <Form.Control
            type="text"
            placeholder="Number"
            value={partNumberSearchValue}
            onChange={e => setPartNumberSearchValue(e.target.value)}
            style={{ marginRight: 10 }}
          />
          <Form.Control
            type="text"
            placeholder="Description"
            value={partDescriptionSearchValue}
            onChange={e => setPartDescriptionSearchValue(e.target.value)}
            style={{ marginRight: 10 }}
          />
          <Button
            type="button"
            variant="primary"
            onClick={searchPackaging}
            disabled={isSearching ||
              (!partNumberSearchValue.trim().length && !partDescriptionSearchValue.trim().length)}
          >
              <span style={styles.partsModalSearchButton}>
                <FontAwesomeIcon icon={faSearch} style={{ marginRight: 4 }}/>
                Search
              </span>
          </Button>
        </div>
        {
          hasSearched ?
            <h5 style={styles.headingResultsText}>
              {searchResults.length === 100 ?
                'Found over 100 matching packaging types' :
                `Found ${searchResults.length} matching packaging types`}
            </h5> :
            null
        }
      </div>
      <div style={styles.partsModalContent}>
        {
          isSearching ?
            <div style={styles.spinnerContainer}>
              <Spinner animation="border"/>
              <p style={styles.spinnerText}>Searching...</p>
            </div> :
            searchResults.length ?
              <Table striped>
                <thead>
                <tr>
                  <th>Number</th>
                  <th>Description</th>
                  <th>Cost</th>
                </tr>
                </thead>
                <tbody>
                {searchResults.map((row, index) => (
                  <tr
                    key={index}
                    style={styles.clickableRow}
                    onClick={() => {
                      onSubmit(row);
                      setPartNumberSearchValue('');
                      setPartDescriptionSearchValue('');
                      setSearchResults([]);
                      setHasSearched(false);
                      hide();
                    }}
                  >
                    <td>{row.partNumber}</td>
                    <td>{row.partDescription}</td>
                    <td>{row.partCost != null ? round(row.partCost, 5) : 'N/A'}</td>
                  </tr>
                ))}
                </tbody>
              </Table> :
              null
        }
      </div>
    </Modal>
  );
};

const styles = {
  partsModal: {
    content: {
      top: 25,
      bottom: 25,
      left: 25,
      right: 25,
      borderRadius: 5,
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      justifyContent: 'flex-start',
    },
  },
  partsModalContent: {
    flex: 1,
    overflowY: 'auto',
  },
  partsModalHeaderRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: 10,
    borderBottom: 'solid 1px #CCCCCC',
  },
  partsModalSearchGroup: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  partsModalSearchButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  closeTimes: {
    cursor: 'pointer',
  },
  spinnerContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: 30,
  },
  spinnerText: {
    margin: 0,
    marginTop: 10,
    fontSize: 20,
  },
  headingResultsText: {
    margin: 0,
    alignSelf: 'center',
  },
  clickableRow: {
    cursor: 'pointer',
  },
};

export default PackagingCostsSearchModal;